import { useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from "react-i18next";

const ProjectModal = ({ toggleModal, project }) => {
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const { t } = useTranslation();
  
    const handleImageClick = (index) => {
      setCurrentImageIndex(index);
      setFullscreenImage(project.screenshots[index]);
    };
  
    const handlePrevImage = (e) => {
      e.stopPropagation();
      const newIndex = (currentImageIndex - 1 + project.screenshots.length) % project.screenshots.length;
      setCurrentImageIndex(newIndex);
      setFullscreenImage(project.screenshots[newIndex]);
    };
  
    const handleNextImage = (e) => {
      e.stopPropagation();
      const newIndex = (currentImageIndex + 1) % project.screenshots.length;
      setCurrentImageIndex(newIndex);
      setFullscreenImage(project.screenshots[newIndex]);
    };
  
    const handleClose = () => {
      setFullscreenImage(null);
    };
  
    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget) {
        toggleModal();
      }
    };
  
    return (
      <>
        <div 
          className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-gray-800/70"
          onClick={handleBackdropClick}
        >
          <div className="relative p-6 w-full max-w-3xl rounded-lg border shadow-lg bg-background border-secondary">
            {/* Close Button */}
            <button
              className="flex absolute top-2 right-2 justify-center items-center p-2 rounded-full transition duration-200 text-primary hover:bg-gray-200 dark:hover:bg-gray-700"
              onClick={toggleModal}
            >
              <CloseIcon />
            </button>
  
            {/* Project Title */}
            <div className="flex flex-col mb-6 sm:flex-row sm:items-center">
              <h2 className="pr-4 text-3xl font-bold text-primary">{project.title}</h2>
              {project.isOngoing ? (
                <button
                  disabled
                  className="px-1 w-32 text-white bg-gray-400 rounded transition duration-200 cursor-not-allowed sm:w-auto sm:px-4 sm:py-2"
                >
                  {t("code_not_available")}
                </button>
              ) : (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-1 w-32 text-white bg-blue-600 rounded transition duration-200 sm:w-auto sm:px-4 sm:py-2 hover:bg-blue-700"
                >
                  {project.isLive ? t("check_live_site") : t("check_code")}
                </a>
              )}
            </div>

            {/* Tech Stack */}
            <div className="mb-4">
              <h3 className="mb-2 text-lg font-semibold text-primary">{t("tech_stack")}</h3>
              <div className="flex flex-wrap gap-2">
                {project.stacks.map((tech, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 text-sm text-blue-800 bg-blue-100 rounded-full dark:bg-blue-900 dark:text-blue-100"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Screenshots */}
            <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2">
              {project.screenshots.map((screenshot, index) => (
                <img
                  key={index}
                  src={screenshot}
                  alt={`Screenshot ${index + 1}`}
                  className={`object-cover w-full h-48 transition duration-200 cursor-pointer rounded-lg hover:opacity-75 
                    ${project.screenshots.length === 3 && index === 2 ? "sm:col-span-2" : ""}`}
                  onClick={() => handleImageClick(index)}
                />
              ))}
            </div>
  
            {/* Project Description */}
            <p className="mb-4 text-primary">{t(project.description)}</p>
          </div>
        </div>
  
        {/* Image Viewer Dialog */}
        <Dialog
          open={Boolean(fullscreenImage)}
          onClose={handleClose}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
        >
          <div className="relative flex items-center justify-center bg-black/90 min-h-[80vh]">
            {/* Close button */}
            <IconButton
              onClick={handleClose}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
              size="large"
            >
              <CloseIcon />
            </IconButton>

            {/* Navigation buttons */}
            {project.screenshots.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  className="absolute left-4 text-white hover:text-gray-300"
                  size="large"
                >
                  <ArrowBackIcon />
                </IconButton>

                <IconButton
                  onClick={handleNextImage}
                  className="absolute right-4 text-white hover:text-gray-300"
                  size="large"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </>
            )}

            {/* Image */}
            <img
              src={fullscreenImage}
              alt="Fullscreen"
              className="max-w-full max-h-[80vh] object-contain"
            />

            {/* Image counter */}
            {project.screenshots.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white">
                {currentImageIndex + 1} / {project.screenshots.length}
              </div>
            )}
          </div>
        </Dialog>
      </>
    );
};

export default ProjectModal;